import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ShieldAlert, Target, CheckCircle2, AlertTriangle, Trophy } from "lucide-react";
import { submitSandbox } from "../../lib/api";
import SplitVisualizer from "./SplitVisualizer";

const SEV_COLOR = {
  low: "text-emerald-300 border-emerald-300/30 bg-emerald-300/5",
  med: "text-yellow-300 border-yellow-300/30 bg-yellow-300/5",
  high: "text-orange-300 border-orange-300/40 bg-orange-300/5",
  crit: "text-red-300 border-red-300/40 bg-red-300/10",
};

export const HuntModal = ({ bounty, onClose, onAccepted }) => {
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const lines = bounty?.artifact_lines || [];

  const toggle = (idx) => {
    if (result) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const submit = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const r = await submitSandbox(bounty.id, Array.from(selected));
      setResult(r);
      onAccepted && onAccepted(r);
    } catch (e) {
      alert("Submission failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const correctSet = useMemo(() => new Set(result?.correct_lines || []), [result]);
  const missedSet  = useMemo(() => new Set(result?.missed_lines || []), [result]);
  const fpSet      = useMemo(() => new Set(result?.false_positive_lines || []), [result]);

  const lineClass = (idx) => {
    if (result) {
      if (correctSet.has(idx)) return "border-emerald-400/60 bg-emerald-400/10 text-emerald-100";
      if (missedSet.has(idx))  return "border-red-400/60 bg-red-400/10 text-red-200";
      if (fpSet.has(idx))      return "border-orange-400/60 bg-orange-400/10 text-orange-200";
      return "border-white/10 bg-white/2 text-white/60";
    }
    if (selected.has(idx)) return "border-red-400 bg-red-400/15 text-red-100";
    return "border-white/10 bg-black/40 text-white/85 hover:border-yellow-300/40 hover:bg-yellow-300/5";
  };

  const lineIcon = (idx) => {
    if (!result) return null;
    if (correctSet.has(idx)) return <CheckCircle2 size={14} className="text-emerald-300" />;
    if (missedSet.has(idx))  return <AlertTriangle size={14} className="text-red-300" />;
    if (fpSet.has(idx))      return <AlertTriangle size={14} className="text-orange-300" />;
    return null;
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-start md:items-center justify-center p-3 md:p-6 overflow-y-auto"
        data-testid="hunt-modal"
      >
        <motion.div
          initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
          className="w-full max-w-3xl rounded-2xl border border-yellow-300/20 bg-gradient-to-b from-zinc-950 to-black shadow-[0_30px_80px_rgba(0,0,0,0.7)] my-4"
        >
          {/* Header */}
          <div className="flex items-start justify-between p-5 border-b border-white/10">
            <div className="flex items-start gap-3">
              <div className="h-12 w-12 rounded-xl bg-red-500/15 border border-red-400/40 flex items-center justify-center">
                <ShieldAlert className="text-red-300" size={22} />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-yellow-300/70 font-display">
                  EIGOO Sandbox · Threat Hunt
                </div>
                <h3 className="font-display font-black text-xl text-white">{bounty.title}</h3>
                <div className="mt-2 flex items-center gap-2 text-xs">
                  <span className={`px-2 py-0.5 rounded border uppercase font-bold ${SEV_COLOR[bounty.severity] || SEV_COLOR.med}`}>
                    {bounty.severity}
                  </span>
                  <span className="text-white/50">Bounty pool: <span className="text-yellow-200 font-bold">${bounty.payout_usd}</span></span>
                </div>
              </div>
            </div>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-white/60 hover:text-white" data-testid="hunt-close">
              <X size={18} />
            </button>
          </div>

          {/* Description */}
          <div className="px-5 pt-4 text-sm text-white/70">{bounty.description}</div>

          {/* Artifact viewer */}
          <div className="p-5">
            <div className="text-[10px] uppercase tracking-[0.3em] text-cyan-300/70 mb-2 font-display">
              Artifact · {bounty.artifact_kind === "log" ? "Server Log" : "Source Snippet"}
            </div>
            <div className="rounded-xl border border-white/10 bg-black/60 p-2 font-mono text-xs space-y-1 max-h-[40vh] overflow-y-auto">
              {lines.map((ln, idx) => (
                <button
                  key={idx}
                  onClick={() => toggle(idx)}
                  disabled={!!result}
                  data-testid={`hunt-line-${idx}`}
                  className={`w-full text-left px-3 py-1.5 rounded-md border transition flex items-start gap-2 ${lineClass(idx)} ${result ? "cursor-default" : "cursor-pointer"}`}
                >
                  <span className="opacity-40 select-none w-6 shrink-0">{String(idx).padStart(2, " ")}</span>
                  <span className="flex-1 break-all whitespace-pre-wrap">{ln}</span>
                  {lineIcon(idx)}
                </button>
              ))}
            </div>
            <div className="mt-2 text-[11px] text-white/45">
              Tap any line you suspect contains a vulnerability. False positives reduce score.
            </div>
          </div>

          {/* Footer / Result */}
          {!result ? (
            <div className="p-5 border-t border-white/10 flex items-center justify-between gap-3">
              <div className="text-sm text-white/65">
                Flagged: <span className="text-yellow-200 font-bold">{selected.size}</span> line{selected.size !== 1 ? "s" : ""}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={onClose}
                  className="px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-white/80 font-display font-bold text-sm border border-white/10"
                  data-testid="hunt-cancel"
                >
                  Cancel
                </button>
                <button
                  onClick={submit}
                  disabled={busy || selected.size === 0}
                  className="px-5 py-2 rounded-lg bg-gradient-to-br from-red-500 to-red-700 disabled:opacity-40 hover:brightness-110 text-white font-display font-black text-sm flex items-center gap-2 shadow-[0_8px_24px_rgba(239,68,68,0.35)]"
                  data-testid="hunt-submit"
                >
                  <Target size={16} /> {busy ? "Hunting…" : "Submit Findings"}
                </button>
              </div>
            </div>
          ) : (
            <div className="p-5 border-t border-white/10 space-y-4" data-testid="hunt-result">
              <div className="flex items-center gap-3">
                <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${result.status === "accepted" ? "bg-emerald-500/15 border border-emerald-400/40" : "bg-red-500/15 border border-red-400/40"}`}>
                  <Trophy className={result.status === "accepted" ? "text-emerald-300" : "text-red-300"} size={22} />
                </div>
                <div>
                  <div className="font-display font-black text-2xl text-white">
                    {result.status === "accepted" ? "BOUNTY ACCEPTED" : "REJECTED"}
                  </div>
                  <div className="text-sm text-white/60">
                    Score <span className="text-yellow-200 font-bold">{result.score}</span> · earned
                    <span className="text-yellow-200 font-bold ml-1">${result.payout_usd?.toFixed?.(2)}</span>
                    {" · "}+{result.xp_awarded} XP · +{result.coins_awarded} coins
                  </div>
                </div>
              </div>
              <SplitVisualizer
                payout={result.payout_usd || 0}
                hunter={result.hunter_cut_usd || 0}
                treasury={result.treasury_cut_usd || 0}
                donation={result.donation_cut_usd || 0}
              />
              <div className="text-[11px] text-cyan-200/70 italic border-l-2 border-cyan-300/40 pl-3">
                10% of every bounty flows to the CyberKids Donation Pool — every hunt strengthens the next cadet.
              </div>
              <div className="flex justify-end">
                <button
                  onClick={onClose}
                  className="px-5 py-2 rounded-lg bg-gradient-to-br from-yellow-300 to-yellow-600 text-black font-display font-black text-sm"
                  data-testid="hunt-done"
                >
                  Return to Vault
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default HuntModal;
